import { ReactNode, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { groupBy, map, sortBy } from "lodash-es";
import { FlagTypes } from "~/components/ui-library";
import { format, formatISO, parseISO } from "~/lib/date";
import {
  ExceptionMutationAction,
  ExceptionReviewAction,
  ExceptionStatus,
  ExceptionType,
  useApplyExceptionExtensionReviewMutation,
  useApplyExceptionReviewMutation,
} from "~/operations";
import { ExceptionTimeline } from "~/components/exceptions/timeline";
import {
  ExceptionTimelineItem,
  ExceptionTimelineItemProps,
} from "~/components/exceptions/timeline-item";
import { useSearchParams } from "react-router-dom";
import { EmptyState } from "~/components/empty-state/empty-state";
import { InternalRefetchQueryDescriptor } from "@apollo/client/core/types";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";
import { ExceptionListWithPagination } from "~/components/exceptions/ExceptionListWithPagination";
import {
  ExceptionsView,
  ExceptionsViewSwitcher,
} from "~/components/exceptions/ExceptionsViewSwitcher";
import { Loading } from "../loading";
import { getTargetFromExceptionType } from "~/pages/findings/Exceptions/lib/getTargetFromExceptionType";
import { ScopeType, spaceIdFromMrn, useScope } from "~/hooks/useScope";
import ExtensionHistoryTable from "~/pages/findings/Exceptions/components/ExtensionHistoryTable";
import { spaceMrnFromExceptionGroup } from "~/lib/mrn";
import { ScopeProvider } from "~/providers/ScopeProvider";
import { ExceptionsFilterVariables } from "~/pages/shared/policy/hooks/usePolicyExceptionsFilterContext";

interface Props {
  emptyStateType: string;
  refetchQueries: InternalRefetchQueryDescriptor[];
  exceptionGroups: ExceptionGroup[];
  renderTable: (exceptionGroup: ExceptionGroup) => ReactNode;
  isLoading: boolean;
  findingMrn?: string;
  exceptionTypes: ExceptionType[];
  filterVariables?: ExceptionsFilterVariables;
  assetMrn?: string;
  hideViewSwitcher?: boolean;
}

export function Exceptions({
  emptyStateType,
  refetchQueries,
  exceptionGroups,
  renderTable,
  isLoading,
  findingMrn,
  exceptionTypes,
  filterVariables,
  assetMrn,
  hideViewSwitcher,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const { activeScope } = useScope();
  const isPlatformScope = activeScope?.type === ScopeType.Region;

  const [applyExceptionReview, { loading: isReviewLoading }] =
    useApplyExceptionReviewMutation();
  const [loadingAction, setLoadingAction] = useState<
    ExceptionReviewAction | undefined
  >();
  const [loadingExceptionId, setLoadingExceptionId] = useState<
    string | undefined
  >();

  const [selectedView, setSelectedView] = useState<ExceptionsView>(
    ExceptionsView.List,
  );

  const [applyExceptionExtensionReview, { loading: isExtensionReviewLoading }] =
    useApplyExceptionExtensionReviewMutation();

  const handleReviewActionChange: ExceptionTimelineItemProps["onReviewActionChange"] =
    async (exceptionGroup, action) => {
      setLoadingAction(action);
      setLoadingExceptionId(exceptionGroup.id);
      const isPendingExtension =
        exceptionGroup.status === ExceptionStatus.NeedsExtension;

      try {
        if (isPendingExtension) {
          await applyExceptionExtensionReview({
            variables: {
              input: {
                exceptionMrn: exceptionGroup.mrn,
                action,
              },
            },
            awaitRefetchQueries: true,
            notifyOnNetworkStatusChange: true,
            refetchQueries,
          });

          enqueueSnackbar("Exception extension review applied successfully", {
            variant: "success",
          });
        } else {
          await applyExceptionReview({
            variables: {
              input: {
                exceptionMrn: exceptionGroup.mrn,
                action,
              },
            },
            notifyOnNetworkStatusChange: true,
            awaitRefetchQueries: true,
            refetchQueries,
          });

          enqueueSnackbar("Exception review applied successfully", {
            variant: "success",
          });
        }
      } catch (error) {
        enqueueSnackbar("Failed to apply exception review", {
          variant: "error",
        });
      } finally {
        setLoadingAction(undefined);
        setLoadingExceptionId(undefined);
      }
    };

  const groupedByDate = groupBy(exceptionGroups, (e) => {
    return formatISO(new Date(e.createdAt), { representation: "date" });
  });

  const focusedExceptionId = searchParams.get("exceptionId");

  const lowerTarget = useMemo(
    () =>
      getTargetFromExceptionType(exceptionGroups[0]?.exceptions[0]?.__typename),
    [exceptionGroups],
  );

  const groups = map(groupedByDate, (exceptions, date) => {
    const title = format(parseISO(date), "MMMM do yyyy");
    const isFeatured = false;
    const isLocked = false;
    const needsReview = exceptions.some(
      (e) => e.status === ExceptionStatus.New,
    );

    const riskAcceptedCount = exceptions.filter(
      (e) => e.action === ExceptionMutationAction.RiskAccepted,
    ).length;
    const disabledCount = exceptions.filter(
      (e) => e.action === ExceptionMutationAction.Disable,
    ).length;
    const workaroundCount = exceptions.filter(
      (e) => e.action === ExceptionMutationAction.Workaround,
    ).length;
    const falsePositiveCount = exceptions.filter(
      (e) => e.action === ExceptionMutationAction.FalsePositive,
    ).length;
    const flags = [
      isFeatured ? FlagTypes.featured : undefined,
      isLocked ? FlagTypes.unlock : undefined,
      needsReview ? FlagTypes.review : undefined,
    ].flatMap((f) => f ?? []);
    const isExpanded = exceptions.some(
      (e) =>
        e.id === searchParams.get("exceptionId") ||
        flags.some((f) => f === FlagTypes.review),
    );

    return {
      date,
      title,
      flags,
      riskAcceptedCount,
      disabledCount,
      workaroundCount,
      falsePositiveCount,
      exceptions,
      exceptionRows: sortBy(exceptions, (exc) => exc.createdAt)
        .reverse()
        .map((e) => {
          const timelineItem = (
            <ExceptionTimelineItem
              key={e.id}
              exceptionGroup={e}
              isReviewLoading={isReviewLoading || isExtensionReviewLoading}
              loadingAction={loadingAction}
              loadingExceptionId={loadingExceptionId}
              onReviewActionChange={handleReviewActionChange}
              renderTable={renderTable}
              target={lowerTarget} // target has to get passed in on this level, because the target is different for each timeline item
              displayScope={isPlatformScope}
            >
              {(e.extensions?.length ?? 0) > 0 && (
                <Box mt={2}>
                  <ExtensionHistoryTable extensions={e.extensions ?? []} />
                </Box>
              )}
            </ExceptionTimelineItem>
          );
          if (isPlatformScope) {
            const spaceMrn = spaceMrnFromExceptionGroup(e);
            const spaceId = spaceIdFromMrn(spaceMrn);
            return (
              <ScopeProvider params={{ spaceId }}>{timelineItem}</ScopeProvider>
            );
          }
          return timelineItem;
        }),
      accordionProps: {
        sx: {
          flex: 1,
        },
        defaultExpanded: isExpanded,
      },
    };
  });

  // If exceptions exist and a specific exception is not focused,
  // expand the first date group.
  const firstGroup = groups[0];
  if (firstGroup && !focusedExceptionId) {
    firstGroup.accordionProps.defaultExpanded = true;
  }

  if (isLoading) {
    return (
      <Box mt={3}>
        <Loading what="Exceptions" />
      </Box>
    );
  }

  const timelines = sortBy(groups, "date").reverse();

  if (!isLoading && !timelines.length) {
    return (
      <Box mt={3}>
        <EmptyState contentType={emptyStateType} />
      </Box>
    );
  }

  const activeView = hideViewSwitcher ? ExceptionsView.List : selectedView;

  return (
    <Box>
      {!hideViewSwitcher && (
        <ExceptionsViewSwitcher
          selectedView={selectedView}
          onViewChange={setSelectedView}
        />
      )}

      {activeView === ExceptionsView.AuditLog && (
        <Box>
          <ExceptionTimeline
            {...{
              timelines,
            }}
          />
        </Box>
      )}

      {activeView === ExceptionsView.List && (
        <ExceptionListWithPagination
          exceptionTypes={exceptionTypes}
          exceptionId={null}
          assetMrn={assetMrn}
          findingMrn={findingMrn}
          handleReviewActionChange={handleReviewActionChange}
          isReviewLoading={isReviewLoading || isExtensionReviewLoading}
          loadingAction={loadingAction}
          loadingExceptionId={loadingExceptionId}
          target={lowerTarget}
          filterVariables={filterVariables}
        />
      )}
    </Box>
  );
}
